import "./searchBar.scss";

import React, { useContext, useEffect, useState } from "react";

import { AccessorySingle } from "./singles/AccessorySingle";
import LocalePriceNoCurrency from "../../utils/priceFormat";
import { ProductSingle } from "./singles/ProductSingle";
import { VentingSingle } from "./singles/VentingSingle";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { IProduct } from "@sbi-international/boutik.gatsby.plugin.services";
import { ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services/dist/src/contexts/servicesContext";
import { AccessorySingleSearch } from "./singles/AccessorySingleSearch";
import { ProductStock } from "@sbi-international/boutik.gatsby.plugin.components";
import { ProductPrice } from "../price/ProductPrice";

interface Product {
    code: string;
    slug: { [key: string]: string };
    fields: any;
    title: { [key: string]: string };
}

interface SearchListProps {
    searchString: string;
    allMagentoProducts: any[];
    isSearchListOpen: boolean;
    allProducts: any[];
    setSearchListOpen: (open: boolean) => void;
    t: (key: string) => string;
}

export default function SearchList({
    searchString,
    allMagentoProducts,
    isSearchListOpen,
    allProducts,
    setSearchListOpen,
    t,
}: SearchListProps) {
    const [products, setProducts] = useState<any[]>([]);
    const [productsData, setProductsData] = React.useState<IProduct[]>();
    const [skus, setSkus] = useState<string[]>([]);
    const [isAfterFetch, setAfterFetch] = useState(false);
    const { navigateTranslated } = usePageLocalization();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    useEffect(() => {
        if (productService) {
            productService.operations.getProducts(skus).then((productData) => {
                if (productData) {
                    setProductsData(productData);
                }
            });
        }
    }, [skus]);

    useEffect(() => {
        if (searchString.length >= 2) {
            const searchParam = encodeURIComponent(
                `limit=60&filter=NOT (category.en-CA.keyword: "/Century/Parts/") AND NOT (category.en-CA.keyword: "/Century/Options/"`
            );
            fetch(`/search?q=${searchString}&${searchParam}`)
                .then((response) => response.json())
                .then((data) => {
                    let searchResults = data?.body?.hits?.hits;
                    searchResults = searchResults.filter((e: any) => {
                        const category = e?._source?.category?.["en-CA"]?.toLowerCase();
                        return ["accessories", "heaters", "venting", "parts"].some((str) =>
                            category.includes(str)
                        );
                    });
                    setSkus(searchResults.map((result: any) => result?._source?.code));
                    setSearchListOpen(true);
                });
        } else {
            setSearchListOpen(false);
        }
    }, [searchString, setSearchListOpen, setSkus, setProducts]);

    useEffect(() => {
        const prodTmp = [];
        if (skus && skus.length > 0) {
            for (let i = 0; i < allProducts.length; i++) {
                if (skus.includes(allProducts[i].code)) {
                    prodTmp.push(allProducts[i]);
                }
            }
        }
        setAfterFetch(true);
        setProducts(prodTmp);
    }, [allProducts, allMagentoProducts, skus]);
    if (isSearchListOpen) {
        return (
            <div className="search-list">
                {products.length > 0 ? (
                    <>
                        {products.map((product, index) => {
                            const category = getItemKey(product?.slug?.en_CA, null)?.key;
                            if (category === "accessories" && product?.code) {
                                return <AccessorySingleSearch key={index} product={product} />;
                            } else if (category === "heaters" && product?.code) {
                                return (
                                    <ProductSingle
                                        key={index}
                                        product={product}
                                        isDiscontinued={false}
                                    />
                                );
                            } else if (category === "venting" && product?.code) {
                                return <VentingSingle key={index} product={product} />;
                            } else if (category === "parts") {
                                return (
                                    <PartSingle
                                        key={`search-list-partsingle-${index}`}
                                        product={product}
                                    />
                                );
                            } else if (category === "archives") {
                                return (
                                    <ProductSingle
                                        key={index}
                                        product={product}
                                        isDiscontinued={true}
                                    />
                                );
                            } else return undefined;
                        })}
                        {products.length > 15 && (
                            <div
                                className="see-more"
                                onClick={() => navigateTranslated(`/s/?q=${searchString}`)}
                            >
                                {t("search-more")}
                            </div>
                        )}{" "}
                    </>
                ) : (
                    isAfterFetch && (
                        <p style={{ textAlign: "start", margin: "0.8rem" }}>
                            {t("search-no-results")}
                        </p>
                    )
                )}
            </div>
        );
    } else {
        return <div style={{ display: "none" }}></div>;
    }
}

function getItemKey(slugs: string, item: any) {
    if (!slugs) {
        return { key: "NR", item };
    }
    if (slugs.includes("/cms/blog")) {
        return { key: "cms", item };
    } else if (slugs.includes("/accessories/")) {
        return { key: "accessories", item };
    } else if (slugs.includes("/archives/") || slugs.includes("/discontinued/")) {
        return { key: "archives", item };
    } else if (slugs.includes("/heaters/")) {
        return { key: "heaters", item };
    } else if (slugs.includes("/parts/")) {
        return { key: "parts", item };
    } else if (slugs.includes("/venting/")) {
        return { key: "venting", item };
    } else if (slugs.includes("/options/")) {
        return { key: "accessories", item };
    } else {
        return { key: "NR", item };
    }
}

function PartSingle({ product }: { product: Product }) {
    const { navigateTranslated, currentLocale } = usePageLocalization();
    const [productData, setProductData] = React.useState<IProduct>();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    useEffect(() => {
        if (productService) {
            productService.operations.getProduct(product.code).then((productData) => {
                if (productData) {
                    setProductData(productData);
                }
            });
        }
    }, [product]);

    return (
        <div
            className="product search-part"
            onClick={() => {
                navigateTranslated(`/s/?q=${product.code}`);
            }}
        >
            <div className="product-info">
                <span className="code">{product.code}</span>
                <span className="title">{product?.title?.[currentLocale]}</span>
                <ProductPrice
                    currentPrice={productData?.currentPrice}
                    prefixClassName="search-list-price"
                />
                <div className="search-list-price">
                    <ProductStock currentStock={productData?.currentStock} type="small" isCustomMade={false} />
                </div>
            </div>
        </div>
    );
}
